// As ferramentas do assistente: o que ele pode perguntar ao modelo.
//
// Cada uma corre AQUI, no browser, sobre o modelo que o utilizador já
// tem aberto. O servidor nunca vê o IFC; só recebe a pergunta e o texto
// que estas funções devolvem. É o que permite prometer que o modelo do
// cliente não sai da máquina dele.
//
// A leitura em si vem do @vn2r/ifc-read, partilhado com o servidor MCP.
// O que é desta camada é a apresentação: o texto sai em português,
// porque é português que o assistente fala ao utilizador.
//
// Regra ao escrever uma ferramenta nova: devolver factos, nunca juízos.
// "38 portas sem FireRating" é um facto; "a modelação está má" não é, e
// não é ao assistente que compete dizê-lo.

import {
  readGeoRef,
  readSpatial,
  readUnits,
  type GeoRefInfo,
} from '@vn2r/ifc-read'
import { rawIfc } from '@viewer/lib/rawIfc'
import {
  buildIdsIndexes,
  buildView,
  type ElementView,
  type IdsIndexes,
  type PropView,
} from '@modules/ids/lib/idsEngine'
import { ensureLoaded, SemIfcError, sourceFor } from './source'

/** Definição enviada ao modelo. O esquema vive aqui, num sítio só; o
 *  servidor valida apenas os NOMES contra a sua lista. */
export interface ToolDef {
  name: string
  description: string
  input_schema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: string[]
  }
}

export const TOOL_DEFS: ToolDef[] = [
  {
    name: 'resumo_modelo',
    description:
      'Resumo do modelo aberto: esquema IFC, unidades declaradas, nome do ' +
      'projeto, local e edifícios, lista de pisos com as suas cotas, e ' +
      'contagem de elementos por classe IFC. Usa isto para responder a ' +
      '"o que tem este modelo" ou "quantos X existem".',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'georreferenciacao',
    description:
      'Georreferenciação do modelo aberto: se tem IfcMapConversion, o ' +
      'sistema de coordenadas (IfcProjectedCRS), a origem projetada ' +
      '(eastings, northings, altura), a rotação do norte da quadrícula, a ' +
      'escala, a latitude e longitude do IfcSite, e o norte verdadeiro.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'procurar_elementos',
    description:
      'Lista elementos de uma classe IFC no modelo aberto, com GlobalId e ' +
      'nome. Usa o nome exato da classe em maiúsculas, por exemplo ' +
      'IFCWALL, IFCDOOR, IFCPIPESEGMENT. Devolve a contagem total mesmo ' +
      'quando corta a lista. Pode filtrar por propriedade: para as ' +
      'paredes exteriores usa classe IFCWALL, propriedade IsExternal e ' +
      'valor true.',
    input_schema: {
      type: 'object',
      properties: {
        classe: {
          type: 'string',
          description: 'Classe IFC em maiúsculas, ex.: IFCDOOR.',
        },
        propriedade: {
          type: 'string',
          description:
            'Nome da propriedade a filtrar, ex.: IsExternal, LoadBearing, ' +
            'FireRating, NetSideArea. Aceita também Pset.Nome.',
        },
        valor: {
          type: 'string',
          description:
            'Valor que a propriedade tem de ter, ex.: true. Se for ' +
            'omitido, basta a propriedade existir com valor.',
        },
        ausente: {
          type: 'boolean',
          description:
            'Inverte o filtro: devolve os elementos que NÃO têm a ' +
            'propriedade (ou que a têm sem valor). Usa isto para ' +
            'perguntas como "que paredes não têm área".',
        },
        limite: {
          type: 'integer',
          description: 'Quantos elementos listar (1 a 200). Por omissão 25.',
        },
      },
      required: ['classe'],
    },
  },
  {
    name: 'somar_quantidade',
    description:
      'Soma uma quantidade sobre os elementos de uma classe, com filtro ' +
      'opcional por propriedade. "quantidade" é o que se soma; ' +
      '"propriedade" e "valor" são apenas o filtro, e ficam vazios se ' +
      'não houver filtro nenhum.\n' +
      'Exemplos exactos:\n' +
      '- área das paredes exteriores: classe IFCWALL, quantidade ' +
      'NetSideArea, propriedade IsExternal, valor true.\n' +
      '- área de construção por piso: classe IFCSLAB, quantidade ' +
      'GrossArea, por_piso true, sem propriedade nem valor.\n' +
      '- área útil por piso: classe IFCSPACE, quantidade NetFloorArea, ' +
      'por_piso true.\n' +
      'Se a quantidade não existir, a resposta diz quais existem: ' +
      'repete então com uma delas. Diz sempre quantos elementos entraram ' +
      'na conta e quantos ficaram de fora.',
    input_schema: {
      type: 'object',
      properties: {
        classe: { type: 'string', description: 'Classe IFC em maiúsculas.' },
        quantidade: {
          type: 'string',
          description: 'Nome da quantidade a somar, ex.: NetSideArea.',
        },
        propriedade: {
          type: 'string',
          description: 'Propriedade a filtrar, ex.: IsExternal.',
        },
        valor: { type: 'string', description: 'Valor exigido, ex.: true.' },
        por_piso: {
          type: 'boolean',
          description:
            'Reparte o resultado por piso, além do total. Usa isto ' +
            'sempre que a pergunta disser "por piso" ou "de cada piso".',
        },
        piso: {
          type: 'string',
          description:
            'Limita a soma a um piso. Basta parte do nome, ex.: "Piso 0" ' +
            'ou "térreo". Usa resumo_modelo para veres os nomes dos ' +
            'pisos deste modelo.',
        },
      },
      required: ['classe', 'quantidade'],
    },
  },
  {
    name: 'selecionar_elementos',
    description:
      'Selecciona elementos no visualizador 3D e opcionalmente isola-os ' +
      '(esconde tudo o resto). PREFERE indicar a classe: para isolar as ' +
      'lajes usa classe IFCSLAB, para as paredes IFCWALL. Só usa ' +
      'global_ids quando queres exactamente os elementos de uma consulta ' +
      'que acabaste de fazer nesta mesma resposta. Devolve sempre as ' +
      'classes que ficaram seleccionadas: confirma-as antes de dizer ao ' +
      'utilizador o que seleccionaste.',
    input_schema: {
      type: 'object',
      properties: {
        classe: {
          type: 'string',
          description:
            'Classe IFC a seleccionar, em maiúsculas. Ex.: IFCSLAB, ' +
            'IFCWALL, IFCDOOR.',
        },
        propriedade: {
          type: 'string',
          description: 'Filtro opcional, ex.: IsExternal.',
        },
        valor: { type: 'string', description: 'Valor exigido, ex.: true.' },
        ausente: {
          type: 'boolean',
          description: 'Inverte o filtro: os que NÃO têm a propriedade.',
        },
        global_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Alternativa à classe: GlobalId exactos. Não uses GlobalId ' +
            'de perguntas anteriores.',
        },
        isolar: {
          type: 'boolean',
          description:
            'Se verdadeiro, esconde tudo o resto. Por omissão apenas ' +
            'selecciona.',
        },
      },
    },
  },
  {
    name: 'tipos_de_classe',
    description:
      'Agrupa os elementos de uma classe pelos seus TIPOS, com a ' +
      'contagem de cada um e um exemplo (GlobalId) por tipo. Responde a ' +
      '"que tipos de luminárias existem", "quantos tipos de parede há", ' +
      'e é o que se usa antes de isolar UM exemplo de cada tipo: pega ' +
      'nos GlobalId de exemplo e passa-os a selecionar_elementos. Não ' +
      'confundir com procurar_elementos, que lista os elementos um a um.',
    input_schema: {
      type: 'object',
      properties: {
        classe: {
          type: 'string',
          description: 'Classe IFC, ex.: IFCLIGHTFIXTURE.',
        },
      },
      required: ['classe'],
    },
  },
  {
    name: 'propriedades_elemento',
    description:
      'Todos os atributos e property sets de um elemento, identificado ' +
      'pelo GlobalId. Usa isto depois de procurar_elementos, para ' +
      'inspecionar um caso concreto.',
    input_schema: {
      type: 'object',
      properties: {
        global_id: {
          type: 'string',
          description: 'GlobalId do elemento (22 caracteres).',
        },
      },
      required: ['global_id'],
    },
  },
]

const MAX_LISTED = 200
const DEFAULT_LISTED = 25

// --- Filtrar e somar por propriedade ----------------------------------
//
// Reutiliza os índices do motor IDS: ele já lê os IfcRelDefinesByProperties
// e achata property sets E quantidades (IfcElementQuantity) na mesma
// lista. O IsExternal e o NetSideArea chegam pelo mesmo caminho, portanto
// não há aqui leitura de IFC nenhuma, só filtragem.

/** Construir os índices é a parte cara. Guardam-se por modelo, porque a
 *  pergunta seguinte é quase sempre sobre o mesmo. */
const indices = new Map<string, IdsIndexes>()

async function indicesDe(modelId: string): Promise<IdsIndexes> {
  const guardado = indices.get(modelId)
  if (guardado) return guardado
  const novo = await buildIdsIndexes(modelId)
  indices.set(modelId, novo)
  return novo
}

/** Esquece o que estava indexado. Chamado quando os modelos mudam, para
 *  não responder sobre um IFC que já ninguém tem aberto. */
export function forgetAssistantIndexes(modelId?: string): void {
  if (modelId) {
    indices.delete(modelId)
    pisosPorModelo.delete(modelId)
    classesPorModelo.delete(modelId)
  } else {
    indices.clear()
    pisosPorModelo.clear()
    classesPorModelo.clear()
  }
}

/** Normaliza um valor para comparação tolerante.
 *
 *  Os booleanos IFC aparecem de várias formas conforme quem exportou:
 *  'true', 'T', '.T.', '1'. Um utilizador escreve "true" ou "sim". Fazer
 *  a comparação literal daria "nenhuma parede exterior" num modelo cheio
 *  delas, que é exactamente o tipo de resposta errada com ar de certa
 *  que não queremos. */
export function normaliza(v: string | null): string {
  if (v === null) return ''
  const s = v.trim().toLowerCase().replace(/^\.|\.$/g, '')
  if (s === 't' || s === 'true' || s === 'sim' || s === '1') return 'true'
  if (s === 'f' || s === 'false' || s === 'nao' || s === 'não' || s === '0') {
    return 'false'
  }
  return s
}

/** A propriedade pedida bate certo nesta célula? Aceita só o nome
 *  ('IsExternal') ou qualificado ('Pset_WallCommon.IsExternal'). */
export function propBate(pedido: string, p: PropView): boolean {
  const alvo = pedido.trim().toLowerCase()
  if (!alvo) return false
  const nome = p.name.toLowerCase()
  const completo = `${p.pset}.${p.name}`.toLowerCase()
  return alvo === nome || alvo === completo
}

interface Filtrado {
  expressID: number
  view: ElementView
}

// --- Resolver o nome da classe ----------------------------------------
//
// O utilizador escreve "buildingelementproxy" e o modelo passa-o tal e
// qual. A classe chama-se IFCBUILDINGELEMENTPROXY, e procurar à letra
// devolvia zero num modelo com 131 desses elementos: uma resposta
// errada, dada com confiança, sobre um modelo real.
//
// Em vez de exigir o nome exacto, resolve-se contra as classes que o
// modelo tem mesmo. Quando não há maneira de resolver, a resposta diz
// quais existem, para se corrigir na volta seguinte.

const classesPorModelo = new Map<string, Map<string, number>>()

async function classesDo(modelId: string): Promise<Map<string, number>> {
  const guardado = classesPorModelo.get(modelId)
  if (guardado) return guardado
  const mapa = new Map<string, number>()
  try {
    const { viewerApi } = await import('@viewer/lib/viewerApi')
    const model = viewerApi.getModel(modelId)
    if (model) {
      const porCategoria = await model.getItemsOfCategories([/.*/])
      for (const [categoria, ids] of Object.entries(porCategoria)) {
        const n = Array.isArray(ids) ? ids.length : 0
        if (n > 0) mapa.set(categoria.toUpperCase(), n)
      }
    }
  } catch {
    /* sem catálogo: cai-se no nome tal como veio */
  }
  classesPorModelo.set(modelId, mapa)
  return mapa
}

/** Elementos de uma classe segundo o FRAGMENTS, com os ids que a
 *  selecção usa.
 *
 *  Existe porque as duas fontes podem discordar: a lista de classes vem
 *  do Fragments e a procura de elementos vinha do parser web-ifc. Quando
 *  o web-ifc devolvia zero para uma classe que o Fragments conhece, o
 *  assistente afirmava que não havia nada num modelo que a tinha, e
 *  contradizia-se na mesma conversa. Para seleccionar, o Fragments é
 *  ainda a fonte certa: é o seu espaço de ids que a selecção fala. */
export async function alvosDaClasse(
  classe: string,
): Promise<Array<{ modelId: string; localId: number }>> {
  const out: Array<{ modelId: string; localId: number }> = []
  try {
    const { viewerApi } = await import('@viewer/lib/viewerApi')
    for (const [mid, model] of viewerApi.iterModels()) {
      const bucket = await model.getItemsOfCategories([
        new RegExp(`^${classe}$`, 'i'),
      ])
      for (const ids of Object.values(bucket)) {
        if (ids) for (const localId of ids) out.push({ modelId: mid, localId })
      }
    }
  } catch {
    /* sem Fragments: fica a cargo de quem chamou */
  }
  return out
}

export interface ClasseResolvida {
  classe: string | null
  /** Classes presentes no modelo, quando não se conseguiu resolver. */
  disponiveis: string[]
}

export async function resolverClasse(
  modelId: string,
  pedido: string,
): Promise<ClasseResolvida> {
  const cru = pedido.trim().toUpperCase().replace(/\s+/g, '')
  if (!cru) return { classe: null, disponiveis: [] }
  const classes = await classesDo(modelId)
  if (classes.size === 0) {
    // Sem catálogo, o melhor palpite é acrescentar o prefixo em falta.
    return { classe: cru.startsWith('IFC') ? cru : `IFC${cru}`, disponiveis: [] }
  }
  if (classes.has(cru)) return { classe: cru, disponiveis: [] }
  const comPrefixo = cru.startsWith('IFC') ? cru : `IFC${cru}`
  if (classes.has(comPrefixo)) return { classe: comPrefixo, disponiveis: [] }
  // Última tentativa: uma única classe que contenha o que foi pedido.
  const parciais = [...classes.keys()].filter((c) => c.includes(cru))
  if (parciais.length === 1) return { classe: parciais[0], disponiveis: [] }
  return {
    classe: null,
    disponiveis: [...classes.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([c, n]) => `${c} (${n})`),
  }
}

// --- Em que piso está cada elemento -----------------------------------
//
// O ContainerView do motor IDS diz que um elemento está dentro de um
// IfcBuildingStorey, mas não diz QUAL, e para agrupar por piso é o nome
// que faz falta. Constrói-se aqui, directamente das relações, em vez de
// alargar o contrato partilhado por causa de um caso só.

const pisosPorModelo = new Map<string, Map<number, string>>()

function indicePisos(modelId: string): Map<number, string> {
  const guardado = pisosPorModelo.get(modelId)
  if (guardado) return guardado

  const src = sourceFor(modelId)
  const nomeDe = new Map<number, string>()
  for (const id of src.getLineIDsByType('IFCBUILDINGSTOREY')) {
    const s = src.getEntity(id)
    const nome = s ? String((s.Name as { value?: unknown })?.value ?? '') : ''
    nomeDe.set(id, nome || `piso #${id}`)
  }

  const mapa = new Map<number, string>()
  // Duas relações: os elementos construídos entram por
  // ContainedInSpatialStructure, os espaços por Aggregates.
  for (const rel of ['IFCRELCONTAINEDINSPATIALSTRUCTURE', 'IFCRELAGGREGATES']) {
    for (const relId of src.getLineIDsByType(rel)) {
      const r = src.getEntity(relId)
      if (!r) continue
      const estrutura = (r.RelatingStructure ?? r.RelatingObject) as
        | Record<string, unknown>
        | undefined
      const structId = Number(estrutura?.expressID)
      const nome = nomeDe.get(structId)
      if (!nome) continue
      const relacionados = (r.RelatedElements ?? r.RelatedObjects) as
        | unknown[]
        | undefined
      if (!Array.isArray(relacionados)) continue
      for (const e of relacionados) {
        const eid = Number((e as { expressID?: unknown })?.expressID)
        if (Number.isFinite(eid)) mapa.set(eid, nome)
      }
    }
  }
  pisosPorModelo.set(modelId, mapa)
  return mapa
}

async function elementosFiltrados(
  modelId: string,
  classe: string,
  propriedade: string,
  valor: string,
  ausente = false,
  piso = '',
): Promise<{ total: number; escolhidos: Filtrado[] }> {
  const idx = await indicesDe(modelId)
  const src = sourceFor(modelId)
  const ids = src.getLineIDsByType(classe)
  const pisos = piso ? indicePisos(modelId) : null
  const alvoPiso = piso.trim().toLowerCase()
  const escolhidos: Filtrado[] = []
  for (const expressID of ids) {
    if (pisos) {
      const nome = (pisos.get(expressID) ?? '').toLowerCase()
      if (!nome.includes(alvoPiso)) continue
    }
    const view = buildView(modelId, expressID, classe, idx)
    if (propriedade) {
      const celulas = view.properties.filter((p) => propBate(propriedade, p))
      // Tem a propriedade com valor? É esta a pergunta, e o `ausente`
      // apenas a inverte. Sem valor conta como não ter: uma área
      // declarada mas vazia não serve para somar nem para dizer que lá
      // está.
      let tem = celulas.some((c) => c.value !== null)
      if (tem && valor) {
        const alvo = normaliza(valor)
        tem = celulas.some((c) => normaliza(c.value) === alvo)
      }
      if (tem === ausente) continue
    }
    escolhidos.push({ expressID, view })
  }
  return { total: ids.length, escolhidos }
}

function fmt(n: number | null, unit = ''): string {
  if (n === null) return 'não declarado'
  return unit ? `${n} ${unit}` : String(n)
}

function formatGeoRef(g: GeoRefInfo): string {
  const out: string[] = []
  if (g.mapConversion) {
    out.push('IfcMapConversion: presente (georreferenciação completa).')
    if (g.crsName) {
      out.push(
        `Sistema de coordenadas: ${g.crsName}` +
          (g.crsDescription ? ` (${g.crsDescription})` : ''),
      )
    } else {
      out.push('Sistema de coordenadas: não declarado no IfcProjectedCRS.')
    }
    out.push(
      `Origem projetada: eastings ${fmt(g.eastings)}, northings ` +
        `${fmt(g.northings)}, altura ortogonal ${fmt(g.orthogonalHeight)}.`,
    )
    out.push(
      `Rotação do eixo X: ${fmt(g.rotationDeg, 'graus')}. Escala: ` +
        `${fmt(g.scale)}.`,
    )
  } else {
    out.push(
      'IfcMapConversion: ausente. O modelo não tem georreferenciação ' +
        'completa.',
    )
  }
  if (g.latitude !== null || g.longitude !== null) {
    out.push(
      `IfcSite: latitude ${fmt(g.latitude)}, longitude ${fmt(g.longitude)}, ` +
        `elevação ${fmt(g.refElevation)}.`,
    )
  } else {
    out.push('IfcSite: sem latitude nem longitude de referência.')
  }
  out.push(`Norte verdadeiro: ${fmt(g.trueNorthDeg, 'graus a partir do +Y')}.`)
  return out.join('\n')
}

/** Contagens por classe, a partir do que o parser da plataforma sabe
 *  enumerar. Só as classes com pelo menos um elemento. */
function classCounts(modelId: string): Array<{ ifcClass: string; count: number }> {
  const out: Array<{ ifcClass: string; count: number }> = []
  for (const row of rawIfc.countNonGeometricClasses(modelId)) {
    if (row.count > 0) out.push({ ifcClass: row.category, count: row.count })
  }
  return out
}

async function toolResumo(modelId: string): Promise<string> {
  await ensureLoaded(modelId)
  const src = sourceFor(modelId)
  const sp = readSpatial(src)
  const units = readUnits(src)
  const lines: string[] = []
  lines.push(`Projeto: ${sp.projectName ?? 'sem nome'}`)
  lines.push(`Local: ${sp.siteName ?? 'sem nome'}`)
  lines.push(
    `Edifícios: ${sp.buildingNames.length > 0 ? sp.buildingNames.join(', ') : 'nenhum'}`,
  )
  const u = Object.entries(units)
    .map(([k, v]) => `${k} = ${v}`)
    .join(', ')
  lines.push(`Unidades: ${u || 'não declaradas'}`)
  if (sp.storeys.length > 0) {
    lines.push(`Pisos (${sp.storeys.length}), por cota:`)
    for (const s of sp.storeys) {
      lines.push(`  ${s.name ?? '(sem nome)'} @ ${fmt(s.elevation)}`)
    }
  } else {
    lines.push('Pisos: nenhum IfcBuildingStorey no modelo.')
  }
  // Do MESMO catálogo que resolve os nomes das classes e que a lista de
  // entidades da barra lateral mostra. Ter duas contagens diferentes era
  // como o assistente conseguia dizer que não havia uma classe que o
  // painel ao lado mostrava.
  const catalogo = await classesDo(modelId)
  if (catalogo.size > 0) {
    lines.push(`Classes presentes (${catalogo.size}), por contagem:`)
    for (const [classe, n] of [...catalogo.entries()].sort(
      (a, b) => b[1] - a[1],
    )) {
      lines.push(`  ${n}  ${classe}`)
    }
  } else {
    const counts = classCounts(modelId)
    for (const c of counts) lines.push(`  ${c.count}  ${c.ifcClass}`)
  }
  return lines.join('\n')
}

async function toolGeoRef(modelId: string): Promise<string> {
  await ensureLoaded(modelId)
  return formatGeoRef(readGeoRef(sourceFor(modelId)))
}

async function toolProcurar(
  modelId: string,
  input: Record<string, unknown>,
): Promise<string> {
  const pedida = String(input.classe ?? '').trim()
  if (!pedida) return 'Erro: falta a classe IFC.'
  await ensureLoaded(modelId)
  const r = await resolverClasse(modelId, pedida)
  if (!r.classe) {
    return (
      `Não há nenhuma classe chamada "${pedida}" neste modelo. Classes ` +
      `presentes: ${r.disponiveis.slice(0, 40).join(', ')}.`
    )
  }
  const classe = r.classe
  const propriedade = String(input.propriedade ?? '').trim()
  const valor = String(input.valor ?? '').trim()
  const ausente = input.ausente === true || input.ausente === 'true'
  const rawLimite = Number(input.limite)
  const limite = Number.isFinite(rawLimite)
    ? Math.min(Math.max(Math.trunc(rawLimite), 1), MAX_LISTED)
    : DEFAULT_LISTED

  await ensureLoaded(modelId)
  const src = sourceFor(modelId)

  if (!propriedade) {
    const ids = src.getLineIDsByType(classe)
    if (ids.length === 0) {
      // Antes de dizer que não há, perguntar à outra fonte. Dizer "não
      // há" sobre uma classe que o modelo tem é o pior erro possível
      // aqui: parece um facto verificado.
      const alvos = await alvosDaClasse(classe)
      if (alvos.length > 0) {
        return (
          `O modelo tem ${alvos.length} elemento(s) da classe ${classe}, ` +
          `mas o leitor detalhado não os consegue abrir, por isso não ` +
          `posso listar GlobalId nem propriedades. Ainda assim consigo ` +
          `seleccioná-los e isolá-los no 3D com selecionar_elementos.`
        )
      }
      return `Nenhum elemento da classe ${classe} neste modelo.`
    }
    const lines: string[] = [`${ids.length} elemento(s) da classe ${classe}.`]
    for (const id of ids.slice(0, limite)) {
      const e = src.getEntity(id)
      const guid = e
        ? String((e.GlobalId as { value?: unknown })?.value ?? '')
        : ''
      const nome = e ? String((e.Name as { value?: unknown })?.value ?? '') : ''
      lines.push(`  ${guid || '(sem GlobalId)'}  ${nome || '(sem nome)'}`)
    }
    if (ids.length > limite) {
      lines.push(`  (mais ${ids.length - limite} não listados)`)
    }
    return lines.join('\n')
  }

  const { total, escolhidos } = await elementosFiltrados(
    modelId,
    classe,
    propriedade,
    valor,
    ausente,
  )
  const criterio = ausente
    ? `NÃO têm ${propriedade}`
    : valor
      ? `${propriedade} = ${valor}`
      : `${propriedade} presente`
  if (escolhidos.length === 0) {
    return (
      `Nenhum dos ${total} elemento(s) da classe ${classe} ${criterio}. ` +
      `Se esperavas encontrar alguns, confirma o nome da propriedade com ` +
      `a ferramenta propriedades_elemento num deles.`
    )
  }
  const lines: string[] = [
    `${escolhidos.length} de ${total} elemento(s) da classe ${classe} ` +
      `${ausente ? criterio : `cumprem ${criterio}`}.`,
  ]
  for (const { view } of escolhidos.slice(0, limite)) {
    lines.push(
      `  ${view.globalId ?? '(sem GlobalId)'}  ${view.name ?? '(sem nome)'}`,
    )
  }
  if (escolhidos.length > limite) {
    lines.push(`  (mais ${escolhidos.length - limite} não listados)`)
  }
  return lines.join('\n')
}

/** Número em português, com separador de milhares e casas sensatas.
 *
 *  O `String(423.791)` lê-se em Portugal como quatrocentos e vinte e três
 *  mil, que é um erro de três ordens de grandeza numa resposta que parece
 *  bem escrita. */
function numeroPt(n: number): string {
  const casas = Math.abs(n) >= 100 ? 1 : Math.abs(n) >= 1 ? 2 : 4
  return n.toLocaleString('pt-PT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: casas,
  })
}

/** Unidade real da quantidade, lida do projeto e não adivinhada.
 *
 *  Deixar o modelo inferir "m²" a partir do nome da quantidade é o que
 *  faz um projeto em milímetros ser reportado em metros sem ninguém dar
 *  por isso. */
function unidadeDe(
  dataType: string | null,
  units: Record<string, string>,
): string {
  const t = (dataType ?? '').toUpperCase()
  if (t.includes('AREA')) return units.area ?? ''
  if (t.includes('VOLUME')) return units.volume ?? ''
  if (t.includes('LENGTH')) return units.length ?? ''
  if (t.includes('MASS')) return units.mass ?? ''
  return ''
}

/** Não recebe modelId de propósito: procura em TODOS os modelos
 *  carregados, porque numa federação o elemento pode não estar no que
 *  está activo. */
async function toolSelecionar(
  modelId: string,
  input: Record<string, unknown>,
): Promise<string> {
  const isolar = input.isolar === true || input.isolar === 'true'
  const pedida = String(input.classe ?? '').trim()

  let guids: string[]
  if (pedida) {
    await ensureLoaded(modelId)
    const rc = await resolverClasse(modelId, pedida)
    if (!rc.classe) {
      return (
        `Não há nenhuma classe chamada "${pedida}" neste modelo. Classes ` +
        `presentes: ${rc.disponiveis.slice(0, 40).join(', ')}.`
      )
    }
    const classe = rc.classe
    // Caminho preferido: a classe é dita agora, e os elementos são
    // procurados agora. Passar GlobalId de uma pergunta anterior é como
    // se perdem: os do contexto podem ser de outra classe.
    await ensureLoaded(modelId)
    const { escolhidos } = await elementosFiltrados(
      modelId,
      classe,
      String(input.propriedade ?? '').trim(),
      String(input.valor ?? '').trim(),
      input.ausente === true || input.ausente === 'true',
    )
    guids = escolhidos
      .map((e) => e.view.globalId)
      .filter((g): g is string => Boolean(g))
    if (guids.length === 0) {
      // Sem filtro de propriedade, a selecção não precisa do leitor
      // detalhado: o Fragments sabe que elementos são da classe e é o
      // seu espaço de ids que a selecção fala. Este caminho é o que
      // salva as classes que o leitor detalhado não consegue abrir, e
      // que antes davam "não há" num modelo que as tem.
      const semFiltro =
        !String(input.propriedade ?? '').trim() &&
        input.ausente !== true &&
        input.ausente !== 'true'
      if (semFiltro) {
        const alvos = await alvosDaClasse(classe)
        if (alvos.length > 0) {
          const { viewerApi } = await import('@viewer/lib/viewerApi')
          const { useAppStore } = await import('@core/store')
          useAppStore.getState().applyBulkSelection(alvos, 'replace')
          if (isolar) {
            try {
              await viewerApi.isolateSelected()
            } catch {
              return `Seleccionei ${alvos.length} ${classe}, mas não consegui isolá-los.`
            }
            return (
              `Seleccionei e isolei ${alvos.length} elemento(s) da classe ` +
              `${classe}. Usa "Mostrar tudo" para voltar atrás.`
            )
          }
          return `Seleccionei ${alvos.length} elemento(s) da classe ${classe}.`
        }
      }
      return `Não há elementos da classe ${classe} que cumpram o critério.`
    }
  } else {
    const bruto = input.global_ids
    guids = Array.isArray(bruto)
      ? bruto.map((g) => String(g).trim()).filter(Boolean)
      : String(bruto ?? '')
          .split(/[\s,;]+/)
          .map((g) => g.trim())
          .filter(Boolean)
    if (guids.length === 0) {
      return 'Erro: indica a classe a seleccionar, ou os GlobalId.'
    }
  }

  const { viewerApi } = await import('@viewer/lib/viewerApi')
  const { resolveGuidTargets } = await import('@viewer/lib/resolveGuidTargets')
  const { useAppStore } = await import('@core/store')

  // Procura em todos os modelos carregados: numa federação o elemento
  // pode não estar no que está activo.
  const alvos: Array<{ modelId: string; localId: number }> = []
  for (const [mid] of viewerApi.iterModels()) {
    const locais = await resolveGuidTargets(mid, guids)
    for (const localId of locais) alvos.push({ modelId: mid, localId })
  }
  if (alvos.length === 0) {
    return (
      `Não encontrei nenhum desses ${guids.length} elemento(s) nos modelos ` +
      `carregados. Pode ser que os GlobalId não sejam deste modelo.`
    )
  }

  useAppStore.getState().applyBulkSelection(alvos, 'replace')

  // Que classes ficaram MESMO seleccionadas.
  //
  // Sem isto, um modelo que passe GlobalId errados (por exemplo os da
  // pergunta anterior) pode afirmar que isolou as lajes tendo isolado
  // paredes. Dizer o que se seleccionou de facto tira-lhe essa hipótese,
  // e ao utilizador a dúvida.
  const contagem = new Map<string, number>()
  for (const alvo of alvos.slice(0, 500)) {
    const model = viewerApi.getModel(alvo.modelId)
    if (!model) continue
    try {
      const dados = await model.getItemsData([alvo.localId], {
        attributesDefault: true,
        relationsDefault: { attributes: false, relations: false },
      })
      const cru = (dados?.[0] as Record<string, unknown> | undefined)?._category
      const cat =
        typeof cru === 'string'
          ? cru
          : String((cru as { value?: unknown })?.value ?? '')
      const nome = (cat || 'desconhecida').toUpperCase()
      contagem.set(nome, (contagem.get(nome) ?? 0) + 1)
    } catch {
      /* uma falha de leitura não impede a selecção */
    }
  }
  const resumo = [...contagem.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([c, n]) => `${c} ${n}`)
    .join(', ')

  const naoEncontrados =
    alvos.length < guids.length
      ? ` ${guids.length - alvos.length} não foram encontrados.`
      : ''
  const oQue = resumo ? ` Classes seleccionadas: ${resumo}.` : ''

  if (isolar) {
    try {
      await viewerApi.isolateSelected()
    } catch {
      return (
        `Seleccionei ${alvos.length} elemento(s), mas não consegui ` +
        `isolá-los.${oQue}${naoEncontrados}`
      )
    }
    return (
      `Seleccionei e isolei ${alvos.length} elemento(s) no 3D.${oQue} O ` +
      `resto do modelo está escondido; usa "Mostrar tudo" para voltar ` +
      `atrás.${naoEncontrados}`
    )
  }
  return `Seleccionei ${alvos.length} elemento(s) no 3D.${oQue}${naoEncontrados}`
}

async function toolSomar(
  modelId: string,
  input: Record<string, unknown>,
): Promise<string> {
  const pedida = String(input.classe ?? '').trim()
  const quantidade = String(input.quantidade ?? '').trim()
  if (!pedida) return 'Erro: falta a classe IFC.'
  if (!quantidade) return 'Erro: falta o nome da quantidade a somar.'
  await ensureLoaded(modelId)
  const rc = await resolverClasse(modelId, pedida)
  if (!rc.classe) {
    return (
      `Não há nenhuma classe chamada "${pedida}" neste modelo. Classes ` +
      `presentes: ${rc.disponiveis.slice(0, 40).join(', ')}.`
    )
  }
  const classe = rc.classe
  const propriedade = String(input.propriedade ?? '').trim()
  const valor = String(input.valor ?? '').trim()
  const porPiso =
    input.por_piso === true ||
    input.por_piso === 'true' ||
    String(input.agrupar_por ?? '').toLowerCase() === 'piso'
  const soDoPiso = String(input.piso ?? '').trim()

  await ensureLoaded(modelId)
  const { total, escolhidos } = await elementosFiltrados(
    modelId,
    classe,
    propriedade,
    valor,
    false,
    soDoPiso,
  )
  const criterio = (soDoPiso ? ` no piso "${soDoPiso}"` : '') + (propriedade
    ? valor
      ? ` com ${propriedade} = ${valor}`
      : ` com ${propriedade} presente`
    : '')

  if (escolhidos.length === 0) {
    return `Nenhum dos ${total} elemento(s) da classe ${classe}${criterio}.`
  }

  let soma = 0
  let comQuantidade = 0
  let unidade: string | null = null
  const semQuantidade: string[] = []
  for (const { expressID, view } of escolhidos) {
    const celula = view.properties.find(
      (p) => propBate(quantidade, p) && p.value !== null,
    )
    const n = celula ? Number(celula.value) : NaN
    if (celula && Number.isFinite(n)) {
      soma += n
      comQuantidade += 1
      if (!unidade && celula.dataType) unidade = celula.dataType
    } else {
      semQuantidade.push(view.globalId ?? `#${expressID}`)
    }
  }

  if (comQuantidade === 0) {
    // Em vez de mandar procurar noutro sítio, dizer já o que existe. É
    // o que permite corrigir na volta seguinte, sem mais uma ida ao
    // modelo, e serve tanto ao assistente como a quem está a ler.
    const numericas = new Set<string>()
    for (const { view } of escolhidos.slice(0, 50)) {
      for (const p of view.properties) {
        if (p.value !== null && Number.isFinite(Number(p.value))) {
          numericas.add(`${p.pset}.${p.name}`)
        }
      }
    }
    const lista = [...numericas].sort().slice(0, 40)
    return (
      `Os ${escolhidos.length} elemento(s) da classe ${classe}${criterio} ` +
      `não têm nenhuma quantidade chamada "${quantidade}". ` +
      (lista.length > 0
        ? `Quantidades numéricas disponíveis nestes elementos: ` +
          `${lista.join(', ')}. Repete a consulta com uma destas em ` +
          `"quantidade".`
        : `Estes elementos não têm nenhuma propriedade numérica.`)
    )
  }

  const unidades = readUnits(sourceFor(modelId))
  const nomeUnidade = unidadeDe(unidade, unidades)
  const u = nomeUnidade ? ` ${nomeUnidade}` : ''
  const linhas = [
    `Soma de ${quantidade} em ${classe}${criterio}: ` +
      `${numeroPt(soma)}${u}` +
      (nomeUnidade ? '' : ' (o projeto não declara a unidade)'),
    `Somados ${comQuantidade} de ${escolhidos.length} elemento(s) que ` +
      `cumprem o critério, num total de ${total} da classe.`,
  ]

  // Somar uma ÁREA sobre vários pisos sem o dizer é como se produz um
  // número que parece a implantação e é a construção acumulada. O
  // âmbito passa a estar nos dados, e não à mercê de quem os resume.
  if (!soDoPiso && (unidade ?? '').toUpperCase().includes('AREA')) {
    const pisos = indicePisos(modelId)
    const distintos = new Set<string>()
    for (const { expressID } of escolhidos) {
      const nome = pisos.get(expressID)
      if (nome) distintos.add(nome)
    }
    if (distintos.size > 1) {
      linhas.push(
        `ÂMBITO: esta soma atravessa ${distintos.size} pisos ` +
          `(${[...distintos].join(', ')}). É área acumulada de todos ` +
          `eles, NÃO é a área de implantação nem a área de um piso. ` +
          `Para a implantação, repete com piso="<nome do piso térreo>".`,
      )
    }
  }

  if (porPiso) {
    const pisos = indicePisos(modelId)
    const acc = new Map<string, { soma: number; n: number }>()
    for (const { expressID, view } of escolhidos) {
      const celula = view.properties.find(
        (p) => propBate(quantidade, p) && p.value !== null,
      )
      const n = celula ? Number(celula.value) : NaN
      if (!Number.isFinite(n)) continue
      const piso = pisos.get(expressID) ?? '(sem piso atribuído)'
      const a = acc.get(piso) ?? { soma: 0, n: 0 }
      a.soma += n
      a.n += 1
      acc.set(piso, a)
    }
    linhas.push('Por piso:')
    for (const [piso, a] of [...acc.entries()].sort((x, y) =>
      x[0].localeCompare(y[0], 'pt'),
    )) {
      linhas.push(`  ${piso}: ${numeroPt(a.soma)}${u} (${a.n} elemento(s))`)
    }
    if (acc.size === 0) {
      linhas.push('  (nenhum elemento com a quantidade tem piso atribuído)')
    }
  }
  if (semQuantidade.length > 0) {
    linhas.push(
      `ATENÇÃO: ficaram de fora ${semQuantidade.length} elemento(s) por ` +
        `não terem a quantidade ${quantidade}, portanto este total está ` +
        `INCOMPLETO. Diz isto ao utilizador. Para os ver no 3D, chama ` +
        `procurar_elementos com ausente=true e depois ` +
        `selecionar_elementos com os GlobalId devolvidos.`,
    )
    linhas.push(
      `Sem quantidade: ${semQuantidade.slice(0, 10).join(', ')}` +
        (semQuantidade.length > 10 ? ', ...' : ''),
    )
  }
  return linhas.join('\n')
}

/** Etiqueta do tipo, a partir do nome do elemento.
 *
 *  Os exportadores escrevem "Familia:Tipo:123456", em que o último
 *  número é o identificador da INSTÂNCIA. Sem o cortar, cada elemento
 *  parecia um tipo diferente, e uma pergunta como "que tipos de
 *  luminárias há" devolvia a lista dos 25 elementos em vez dos 2 tipos.
 */
export function tipoDoNome(nome: string | null): string {
  const n = (nome ?? '').trim()
  if (!n) return '(sem nome)'
  return n.replace(/:\d+\s*$/, '').trim() || n
}

async function toolTipos(
  modelId: string,
  input: Record<string, unknown>,
): Promise<string> {
  const pedida = String(input.classe ?? '').trim()
  if (!pedida) return 'Erro: falta a classe IFC.'
  await ensureLoaded(modelId)
  const rc = await resolverClasse(modelId, pedida)
  if (!rc.classe) {
    return (
      `Não há nenhuma classe chamada "${pedida}" neste modelo. Classes ` +
      `presentes: ${rc.disponiveis.slice(0, 40).join(', ')}.`
    )
  }
  const classe = rc.classe
  const { total, escolhidos } = await elementosFiltrados(modelId, classe, '', '')
  if (escolhidos.length === 0) {
    const alvos = await alvosDaClasse(classe)
    if (alvos.length > 0) {
      return (
        `O modelo tem ${alvos.length} elemento(s) da classe ${classe}, mas ` +
        `o leitor detalhado não os consegue abrir, por isso não consigo ` +
        `agrupá-los por tipo.`
      )
    }
    return `Nenhum elemento da classe ${classe} neste modelo.`
  }

  const grupos = new Map<string, { n: number; exemplo: string | null }>()
  for (const { view } of escolhidos) {
    const tipo = tipoDoNome(view.name)
    const g = grupos.get(tipo) ?? { n: 0, exemplo: null }
    g.n += 1
    if (!g.exemplo && view.globalId) g.exemplo = view.globalId
    grupos.set(tipo, g)
  }

  const linhas = [
    `${grupos.size} tipo(s) de ${classe}, em ${total} elemento(s):`,
  ]
  for (const [tipo, g] of [...grupos.entries()].sort((a, b) => b[1].n - a[1].n)) {
    linhas.push(`  ${g.n}x  ${tipo}   exemplo: ${g.exemplo ?? '(sem GlobalId)'}`)
  }
  linhas.push(
    'Para isolar um exemplo de cada tipo, chama selecionar_elementos com ' +
      'os GlobalId de exemplo acima e isolar=true. NÃO uses a classe, que ' +
      'seleccionaria todos.',
  )
  return linhas.join('\n')
}

async function toolPropriedades(
  modelId: string,
  input: Record<string, unknown>,
): Promise<string> {
  const guid = String(input.global_id ?? '').trim()
  if (!guid) return 'Erro: falta o GlobalId.'
  await ensureLoaded(modelId)
  const expressID = rawIfc.getLocalIdByGuid(modelId, guid)
  if (expressID === null) {
    return `Não há nenhum elemento com o GlobalId ${guid} neste modelo.`
  }
  const e = rawIfc.getEntity(modelId, expressID) as Record<
    string,
    unknown
  > | null
  if (!e) return `O elemento ${guid} existe mas não foi possível lê-lo.`

  const lines: string[] = [
    `Elemento ${guid} (${String(e.type ?? 'classe desconhecida')}):`,
  ]
  for (const [k, v] of Object.entries(e)) {
    if (k === 'type' || k === 'expressID') continue
    const value = (v as { value?: unknown })?.value ?? v
    if (value === null || value === undefined) continue
    if (typeof value === 'object') continue
    lines.push(`  ${k}: ${String(value)}`)
  }
  return lines.join('\n')
}

/** Executa uma ferramenta pelo nome. Erros voltam como texto: o modelo
 *  precisa de saber que falhou para o poder dizer, em vez de a conversa
 *  morrer sem explicação. */
export async function runTool(
  modelId: string,
  name: string,
  input: Record<string, unknown>,
): Promise<string> {
  try {
    switch (name) {
      case 'resumo_modelo':
        return await toolResumo(modelId)
      case 'georreferenciacao':
        return await toolGeoRef(modelId)
      case 'procurar_elementos':
        return await toolProcurar(modelId, input)
      case 'somar_quantidade':
        return await toolSomar(modelId, input)
      case 'selecionar_elementos':
        return await toolSelecionar(modelId, input)
      case 'tipos_de_classe':
        return await toolTipos(modelId, input)
      case 'propriedades_elemento':
        return await toolPropriedades(modelId, input)
      default:
        return `Erro: ferramenta desconhecida (${name}).`
    }
  } catch (err) {
    // Um modelo sem IFC não é um erro do utilizador nem uma avaria:
    // é uma limitação que tem de ser dita por palavras, e não
    // disfarçada de contagem a zero.
    if (err instanceof SemIfcError) return err.message
    return `Erro ao consultar o modelo: ${
      err instanceof Error ? err.message : String(err)
    }`
  }
}
